import React, { useEffect, useMemo, useState } from 'react';
import { usePagination, useSortBy, useTable } from 'react-table';
import { QueryClient, QueryClientProvider, useQuery } from 'react-query';
import { Col, Row } from 'reactstrap';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { clone } from 'lodash';
import { COCARD_REPORT_COLUMNS } from './columns';
import CoCardReportFilter from './CoCardReportFilter';
import ReactTablePagination from '@/shared/components/table/components/ReactTablePagination';
import {
  reducer,
  useEffectToDispatch,
  fetchApiData,
  fetchApiDataExport,
  initialState,
  Sorting,
} from '@/shared/apiTableHelper';
import { formatYMD, getFirstDay, getReportName } from '@/shared/helpers';

const queryClient = new QueryClient();

const flattenResults = (results) => {
  if (!results) return [];
  const flat = [];
  const extract = (items) => {
    items.forEach((item) => {
      if (item.subRows && item.subRows.length > 0) {
        extract(item.subRows);
      } else {
        flat.push(item);
      }
    });
  };
  extract(Array.isArray(results) ? results : Object.values(results));
  return flat;
};

const DataTable = ({ organization, programs }) => {
  const defaultFrom = formatYMD(getFirstDay());
  const [filter, setFilter] = useState({
    programs,
    from: defaultFrom,
    to: formatYMD(new Date()),
  });
  const [useFilter, setUseFilter] = useState(false);
  const [trigger, setTrigger] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [exportToCsv, setExportToCsv] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const exportLink = React.createRef();

  useEffect(() => {
    setFilter((prev) => ({ ...prev, programs }));
  }, [programs]);

  useEffect(() => {
    if (exportToCsv && exportLink.current) {
      setExportToCsv(false);
      exportLink.current.link.click();
    }
  }, [exportLink]);

  const download = async (filterValues) => {
    try {
      setIsExporting(true);
      const tmpFilter = clone(filterValues);
      tmpFilter.exportToCsv = 1;
      const response = await fetchApiDataExport({
        url: apiUrl,
        filter: tmpFilter,
        sortby: queryPageSortBy,
        trigger: queryTrigger,
      });
      setExportData(response.results);
      setExportHeaders(response.headers);
      setExportToCsv(true);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo(() => COCARD_REPORT_COLUMNS, []);

  const [
    { queryPageIndex, queryPageSize, totalCount, queryPageFilter, queryPageSortBy, queryTrigger },
    dispatch,
  ] = React.useReducer(reducer, initialState);

  const apiUrl = `/organization/${organization.id}/report/co-card-journal-detailed`;

  const { isLoading, error, data, isSuccess } = useQuery(
    ['cocard-report', apiUrl, queryPageIndex, queryPageSize, queryPageFilter, queryPageSortBy, queryTrigger],
    () =>
      fetchApiData({
        url: apiUrl,
        page: queryPageIndex,
        size: queryPageSize,
        filter,
        sortby: queryPageSortBy,
        trigger: queryTrigger,
      }),
    { keepPreviousData: true, staleTime: Infinity }
  );

  const tableData = useMemo(() => flattenResults(data?.results), [data]);
  const totalPageCount = Math.ceil(totalCount / queryPageSize);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        pageIndex: queryPageIndex,
        pageSize: queryPageSize,
        sortBy: queryPageSortBy,
      },
      manualPagination: true,
      pageCount: data ? totalPageCount : null,
      autoResetSortBy: false,
      autoResetPage: false,
    },
    useSortBy,
    usePagination
  );

  const manualPageSize = [];
  useEffectToDispatch(dispatch, { pageIndex, pageSize, gotoPage, sortBy, filter, data, useFilter, trigger });

  if (isLoading || !organization?.id) return <p>Loading...</p>;
  if (error) return <p>Error: {JSON.stringify(error)}</p>;

  if (isSuccess)
    return (
      <div className="table react-table report-table">
        <div className="action-panel">
          <Row className="mx-0">
            <Col lg={12} md={12} sm={12}>
              <CoCardReportFilter
                filter={filter}
                setFilter={setFilter}
                useFilter={useFilter}
                setUseFilter={setUseFilter}
                exportData={exportData}
                exportLink={exportLink}
                exportHeaders={exportHeaders}
                download={download}
                isExporting={isExporting}
                reportName={getReportName('CoCard Report')}
              />
            </Col>
          </Row>
        </div>
        <table {...getTableProps()} style={{ background: '#fff', tableLayout: 'auto' }} className="table table--bordered">
          <thead style={{ top: '60px', zIndex: '2', position: 'sticky', background: '#f9fafb' }}>
            {headerGroups.map((headerGroup, i) => {
              const isColumnRow = i === headerGroups.length - 1;
              return (
                <tr
                  {...headerGroup.getHeaderGroupProps()}
                  style={{ borderBottom: isColumnRow ? '2px solid #e5e7eb' : '1px solid #e5e7eb' }}
                >
                  {headerGroup.headers.map((column, j) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      style={{
                        padding: '10px 12px',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        ...(j > 0 ? { borderLeft: '1px solid #e5e7eb' } : {}),
                      }}
                    >
                      {column.render('Header')}
                      {column.isSorted ? <Sorting column={column} /> : ''}
                    </th>
                  ))}
                </tr>
              );
            })}
          </thead>
          <tbody className="table table--bordered" {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell, j) => (
                    <td
                      {...cell.getCellProps()}
                      style={{
                        textAlign: 'left',
                        padding: '8px 12px',
                        ...(j > 0 ? { borderLeft: '1px solid #e5e7eb' } : {}),
                      }}
                    >
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            {footerGroups.map((footerGroup) => (
              <tr {...footerGroup.getFooterGroupProps()}>
                {footerGroup.headers.map((column) => (
                  <th
                    {...column.getFooterProps()}
                    style={{ padding: '10px 12px', textAlign: 'left', borderTop: '2px solid #e5e7eb', fontWeight: 600 }}
                  >
                    {column.render('Footer')}
                  </th>
                ))}
              </tr>
            ))}
          </tfoot>
        </table>
        {rows.length > 0 && (
          <>
            <ReactTablePagination
              page={page}
              gotoPage={gotoPage}
              previousPage={previousPage}
              nextPage={nextPage}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageOptions={pageOptions}
              pageSize={pageSize}
              pageIndex={pageIndex}
              pageCount={pageCount}
              setPageSize={setPageSize}
              manualPageSize={manualPageSize}
              dataLength={totalCount}
            />
            <div className="pagination justify-content-end mt-2">
              <span>
                Go to page:{' '}
                <input
                  type="number"
                  value={pageIndex + 1}
                  onChange={(e) => {
                    const p = e.target.value ? Number(e.target.value) - 1 : 0;
                    gotoPage(p);
                  }}
                  style={{ width: '100px' }}
                />
              </span>{' '}
              <select
                className="ml-2"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {[10, 20, 30, 40, 50].map((size) => (
                  <option key={size} value={size}>
                    Show {size}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>
    );
};

const TableWrapper = ({ organization, programs }) => {
  if (!organization || !programs) return 'Loading...';
  return (
    <QueryClientProvider client={queryClient}>
      <DataTable organization={organization} programs={programs} />
    </QueryClientProvider>
  );
};

export default withRouter(
  connect((state) => ({ organization: state.organization }))(TableWrapper)
);
